/* ============================================
   usePushNotifications — Web Push subscription
   Manages notification permission, subscription,
   and sync with Supabase push_subscriptions table.
   ============================================ */
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY

/** Convert base64url VAPID key to Uint8Array for PushManager */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const arr = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i)
  return arr
}

interface PushState {
  isSupported: boolean
  permission: NotificationPermission | 'unsupported'
  isSubscribed: boolean
  loading: boolean
  subscribe: () => Promise<boolean>
  unsubscribe: () => Promise<void>
}

export function usePushNotifications(): PushState {
  const { user, token } = useAuth()
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('default')
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [loading, setLoading] = useState(true)

  const isSupported = typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window
    && !!VAPID_PUBLIC_KEY

  // Log support check once
  useEffect(() => {
    console.log('[Push] isSupported:', isSupported,
      '| SW:', 'serviceWorker' in navigator,
      '| PushMgr:', 'PushManager' in window,
      '| Notif:', 'Notification' in window,
      '| VAPID:', !!VAPID_PUBLIC_KEY,
      '| session:', !!token)
  }, [isSupported, token])

  // Check current subscription status
  useEffect(() => {
    if (!isSupported || !token) {
      setLoading(false)
      setPermission(isSupported ? Notification.permission : 'unsupported')
      console.log('[Push] Skipped init — isSupported:', isSupported, '| session:', !!token)
      return
    }

    setPermission(Notification.permission)
    console.log('[Push] Permission:', Notification.permission)

    navigator.serviceWorker.ready.then(async (reg) => {
      try {
        const sub = await reg.pushManager.getSubscription()
        setIsSubscribed(!!sub)
        console.log('[Push] Existing subscription:', !!sub)
      } catch (err) {
        setIsSubscribed(false)
        console.error('[Push] getSubscription error:', err)
      } finally {
        setLoading(false)
      }
    })
  }, [isSupported, token])

  // Subscribe to push notifications
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported || !token) return false

    try {
      setLoading(true)
      console.log('[Push] Starting subscribe flow...')

      // Request permission
      const perm = await Notification.requestPermission()
      setPermission(perm)
      console.log('[Push] Permission result:', perm)
      if (perm !== 'granted') return false

      // Get service worker registration
      const reg = await navigator.serviceWorker.ready
      console.log('[Push] SW ready, subscribing with VAPID key:', VAPID_PUBLIC_KEY?.slice(0, 20) + '...')

      // Subscribe to push
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      })

      const subJson = sub.toJSON()
      const endpoint = subJson.endpoint!
      const p256dh = subJson.keys!.p256dh!
      const auth = subJson.keys!.auth!
      console.log('[Push] Subscribed! endpoint:', endpoint.slice(0, 60) + '...')

      // Save to Supabase (cast: table not in generated types yet)
      const { error } = await (supabase.from('push_subscriptions') as any).upsert(
        {
          user_id: user?.id,
          endpoint,
          p256dh,
          auth,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'endpoint' }
      )

      if (error) {
        console.error('[Push] Error saving subscription:', error)
        return false
      }

      console.log('[Push] ✅ Subscription saved to DB!')
      setIsSubscribed(true)
      return true
    } catch (err) {
      console.error('[Push] Subscribe failed:', err)
      return false
    } finally {
      setLoading(false)
    }
  }, [isSupported, token])

  // Unsubscribe
  const unsubscribe = useCallback(async () => {
    if (!isSupported) return

    try {
      setLoading(true)
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()

      if (sub) {
        // Remove from Supabase
        await (supabase.from('push_subscriptions') as any)
          .delete()
          .eq('endpoint', sub.endpoint)

        // Unsubscribe from browser
        await sub.unsubscribe()
      }

      setIsSubscribed(false)
    } catch (err) {
      console.error('Push unsubscribe failed:', err)
    } finally {
      setLoading(false)
    }
  }, [isSupported])

  return { isSupported, permission, isSubscribed, loading, subscribe, unsubscribe }
}
